import React, { useState, useEffect } from 'react'
import FormStepsNav from '../../2_Molecules/FormStepsNav/FormStepsNav'
import FindDealerStep from '../../4_Templates/FindDealerStep/FindDealerStep'
import ContactDetailsStep from '../../4_Templates/ContactDetailsStep/ContactDetailsStep'
import SelectVehicleStep from '../../4_Templates/SelectVehicleStep/SelectVehicleStep'
import { languageController } from '../../../controllers/language/languageController'
import StepWrapper from '../../2_Molecules/StepWrapper/StepWrapper'
import ConfirmationStep from '../../4_Templates/ConfirmationStep/ConfirmationStep'
import { convertLangCodeToSingleLetter, displayOutsideARBlock, displayOutsideComponents, hideOutsideComponents, pushToGTMDataLayer, scrollToTop, handleAnalyticsTrackingCodes } from '../../../utils/helpers'
import pidController from '../../../controllers/pidController'
import { getEndpointUrl } from '../../../utils/endPoints'
import { Location } from '../../../types'

// Vehicle data interface
export interface VehicleData {
  id?: string | number;
  isEV?: boolean;
  title: string;
  model: string;
  msrp: number;
  year: string;
  modelYear?: string;
  description?: string;
  visual?: any;
  modelCode?: string;
  modelOption?: string;
  isModelPreOrder?: boolean;
  [key: string]: any;
}

// Dealer data interface
export interface DealerDataItem {
  id?: string;
  name: string;
  address?: string;
  address_line_1: string;
  city?: string;
  province?: string;
  postalCode?: string;
  post_code: string;
  phone?: string;
  phone_number?: string;
  email?: string;
  distance?: number;
  latitude?: number;
  longitude?: number;
  dealer_code?: string;
  geolocation: {
    distance: number;
    latitude: number;
    longitude: number;
  };
  [key: string]: any;
}

export interface DealerData {
  dealer: any; // Using any to match the flexible structure from FindADealer
  userPosition?: Location;
  [key: string]: any;
}

// Contact details data interface
export interface ContactDetailsData {
  firstname: string;
  lastname: string;
  email: string;
  postalcode: string;
  optin?: string;
  [key: string]: any;
}

// Form data state interface
export interface FormData {
  vehicleData?: VehicleData | null;
  dealerData?: DealerData | null;
  contactDetails?: ContactDetailsData | null;
}

// Form extra params interface
export interface FormExtraParams {
  source?: string;
  language?: string;
  lastStepBottomContents?: string;
  initStepTopContents?: string;
  introductionText?: string;
  formDisclaimer?: string;
  [key: string]: any;
}

// Component props interface
export interface GetQuoteProps {
  formExtraParams: FormExtraParams;
  preDefinedVehicleData?: VehicleData | null;
  preDefinedDealerData?: DealerData | null;
  preDefinedUserLocation?: Location | null;
  initStep: number;
  containerRef: React.RefObject<HTMLDivElement>;
}

const GetQuote: React.FC<GetQuoteProps> = ({
  formExtraParams,
  preDefinedVehicleData,
  preDefinedDealerData,
  preDefinedUserLocation,
  initStep,
  containerRef
}) => {
  const { source, language, lastStepBottomContents, initStepTopContents, introductionText, formDisclaimer } = formExtraParams
  const maxStepNum = 3;

  const [currentStep, setCurrentStep] = useState<number>(initStep)
  const [currentProgress, setCurrentProgress] = useState<number>(initStep)
  const [data, setData] = useState<FormData>({
    vehicleData: preDefinedVehicleData || null,
    dealerData: preDefinedDealerData || null,
    contactDetails: null
  })
  const [returnToStart, setReturnToStart] = useState<boolean>(false);

  const step1Title = !returnToStart ? '' : languageController.getCopy('getQuote', 'step1Title') || '';

  // Display top contents on the init step.
  useEffect(() => {
    if (initStep === 0 || initStep > 0) {
      displayOutsideComponents(initStepTopContents)
    }
    // Add the form-related vars to DTM dataLayer.
    pushToGTMDataLayer({ form: 'Get a Quote' });
    pushToGTMDataLayer({ formid: 'GaQ' });
    // If initStep is later than 0, handle analytics code for each step.
    if (initStep > 0) {
      for (let step = 0; step < initStep; step++) {
        setupTrackingCodes(data, step);
      }
    } else {
      setReturnToStart(true);
    }
  }, [])

  useEffect(() => {
    const heroContainer = document.querySelector('.form-hero-container');
    const heroEl = document.getElementById('mz-hero');
    if (!heroContainer || !heroEl || heroEl.parentElement === heroContainer) return;

    const originalParentEl = heroEl.parentElement;
    heroEl.classList.remove('visually-hidden');
    heroContainer.appendChild(heroEl);

    // Restore the hero block when the form is unmounted.
    return () => {
      if (originalParentEl) originalParentEl.appendChild(heroEl);
      heroEl.classList.add('visually-hidden');
    };
  }, [])  

  useEffect(() => {
    // Update 'formstep' dataLayer var when currentStep is changed
    pushToGTMDataLayer({ formstep: `Step${currentStep}` });
    // Handle mz-page-top padding based on current step.
    const containerEl = containerRef.current;
    if (containerEl) {

      const stepsElement = containerEl.querySelector('.mz-get-quote__steps');
      if (stepsElement) {
        if (initStep > 0 && !returnToStart && currentStep > 0) {
          stepsElement.classList.remove('add_margin');
        } else {
          stepsElement.classList.add('add_margin');
        }
      }
    }
  }, [currentStep])

  const handleNextStep = (newProps: Partial<FormData>): void => {
    if (currentStep === maxStepNum) return;

    const nextStep = currentStep + 1;

    // Manage visibility of components outside of the react app.
    if (nextStep === 1) {
      // Hide GaQ Hero and Intro section
      hideOutsideComponents(initStepTopContents)
    } else if (nextStep === maxStepNum) {
      // Display GaQ related bottom contents on confirmation step.
      displayOutsideComponents(lastStepBottomContents)
      // Display AR Block of the selected model
      displayOutsideARBlock(data.vehicleData?.model || '')
    }

    setCurrentStep(nextStep)

    setData({ ...data, ...newProps })
    setupTrackingCodes({ ...data, ...newProps }, currentStep)

    setCurrentProgress(prevState => nextStep > prevState ? nextStep : prevState)

    // Scroll to the top of the screen.
    scrollToTop();
  }

  // Refresh data and go back to init step.
  const refreshSteps = (): void => {
    setReturnToStart(true)
    setCurrentStep(0)
    setCurrentProgress(0)
    setData({})
    displayOutsideComponents(initStepTopContents)
    scrollToTop()
  }

  // Handle adding analytics tracking attributes.
  const setupTrackingCodes = async (data: FormData, completedStep: number): Promise<void> => {
    try {
      // Set vehicle-related tracking attributes
      if (completedStep === 0 && data.vehicleData) {
        const trackData = { modelCarlineName: data.vehicleData.model, modelYear: Number(data.vehicleData.modelYear), msrp: data.vehicleData.msrp };
        handleAnalyticsTrackingCodes(trackData, 'Vehicle Select');
      }
      // Set dealer-related tracking attributes
      if (completedStep === 1 && data.dealerData) {
        const trackData = { dealerName: data.dealerData.dealer?.name };
        handleAnalyticsTrackingCodes(trackData, 'Dealer Select');
      }
      // Set user-related tracking attributes
      if (completedStep === 2 && data.contactDetails) {
        const trackData = { email: data.contactDetails.email, firstname: data.contactDetails.firstname, lastname: data.contactDetails.lastname, postalcode: data.contactDetails.postalcode, optin: data.contactDetails.optin === 'on' ? 'optin' : 'optout' };
        handleAnalyticsTrackingCodes(trackData, 'Contact Details Submit');
      }
    } catch (err) {
      console.log(err)
    }
  }

  return (
    <>
      <FormStepsNav
        isActive={(currentStep > 0 && currentStep < maxStepNum)}
        currentStep={currentStep}
        currentProgress={currentProgress}
        setCurrentStep={setCurrentStep}
        refreshSteps={refreshSteps}
        refreshText={languageController.getCopy('global', 'vehicles') || ''}
        navList={[
          {
            step: 1,
            label: languageController.getCopy('global', 'selectDealerNav') || '',
          },
          {
            step: 2,
            label: languageController.getCopy('global', 'contactDetailsNav') || '',
          },
        ]}
      />
      <div
        className="mz-get-quote"
        data-current-step={currentStep}
      >
        <div className="form-hero-container" />

        <div className="mz-get-quote__steps">
          {/* Step 0: Select a vehicle */}
          <StepWrapper
            className={`mz-get-quote__select-vehicle`}
            stepNum={0}
            currentStep={currentStep}
            currentProgress={currentProgress}
          >
            <SelectVehicleStep
              title={languageController.getCopy('global', 'selectVehicle') || ''}
              handleNextStep={handleNextStep}
              introductionText={introductionText}
            />
          </StepWrapper>
          {/* Step 1: Select a dealer */}
          <StepWrapper
            className={`mz-get-quote__select-dealer`}
            stepNum={1}
            currentStep={currentStep}
            currentProgress={currentProgress}
          >
            <FindDealerStep
              handleNextStep={handleNextStep}
              vehicleData={data.vehicleData || undefined}
              title={step1Title}
              subTitle={languageController.getCopy('global', 'findDealerSub') || ''}
              preDefinedUserLocation={preDefinedUserLocation || undefined}
              preDefinedDealerData={data.dealerData?.dealer ? {
                dealer_code: data.dealerData.dealer.dealer_code || data.dealerData.dealer.id || '',
                name: data.dealerData.dealer.name,
                address_line_1: data.dealerData.dealer.address_line_1 || data.dealerData.dealer.address || '',
                city: data.dealerData.dealer.city,
                post_code: data.dealerData.dealer.post_code || data.dealerData.dealer.postalCode || ''
              } : undefined}
              currentStep={currentStep}
              startingAt={languageController.getCopy('global', 'pricePrefix') || ''}
            />
          </StepWrapper>
          {/* Step 2: Enter Contact Details */}
          <StepWrapper
            className={`mz-get-quote__contact-details`}
            stepNum={2}
            currentStep={currentStep}
            currentProgress={currentProgress}
          >
            <ContactDetailsStep
              handleNextStep={handleNextStep}
              title={languageController.getCopy('global', 'fillContactForm') || ''}
              submitButtonText={languageController.getCopy('getQuote', 'step2Submit') || ''}
              dealerData={data.dealerData || undefined}
              dateAndTimeData={undefined}
              vehicleData={data.vehicleData ? {
                ...data.vehicleData,
                modelYear: data.vehicleData.modelYear || data.vehicleData.year
              } as any : undefined}
              formSource={source || ''}
              formPid={pidController.getPid('requestQuote')}
              formEndPointUrl={getEndpointUrl('request-quote') || ''}
              formLanguage={convertLangCodeToSingleLetter((language || 'en') as 'en' | 'fr')}
              formDisclaimer={formDisclaimer || ''}
            />
          </StepWrapper>
          {/* Step 3: Show Confirmation */}
          <StepWrapper
            className={`mz-get-quote__confirmation`}
            stepNum={3}
            currentStep={currentStep}
            currentProgress={currentProgress}
          >
            <ConfirmationStep
              vehicleData={data.vehicleData ? {
                ...data.vehicleData,
                modelYear: data.vehicleData.modelYear || data.vehicleData.year
              } as any : undefined}
              dealerData={data.dealerData || undefined}
              contactDetails={data.contactDetails || undefined}
            />
          </StepWrapper>
        </div>
      </div>
    </>
  )
}

export default GetQuote
